import { useGetCDM } from "features/cdm/cdm/hooks/useGetCDM";

// Deregistration reason "Died" — enum value sent by DeregisterPatientDialog and
// echoed back on the review detail as reasonCode; reasonDescription is the human
// readable "Died". Match either so we are resilient to the exact BE encoding.
const DIED_REASON_CODE = "DIED";
const DIED_REASON_PATTERN = /died|deceased/i;

const isDiedDeregistration = (review) =>
  review?.reasonCode === DIED_REASON_CODE ||
  DIED_REASON_PATTERN.test(review?.reasonDescription ?? "");

// The most recent deregistration review id (by review date), or null when the
// patient has never been deregistered.
const getLatestDeregistrationId = (deregistrations = []) => {
  const latest = deregistrations.reduce((mostRecent, review) => {
    const date = new Date(review.reviewDate ?? review.date);

    if (Number.isNaN(date.getTime())) return mostRecent;
    if (!mostRecent || date > mostRecent.date) return { id: review.id, date };

    return mostRecent;
  }, null);

  return latest?.id ?? null;
};

// A patient counts as deceased for CDM/OCF/PP when EITHER trigger is true:
//   (a) they were deregistered from CDM/PP with reason "Died", or
//   (b) their patient record is flagged deceased (died flag / date of death).
// The result gates the deceased notice and disables all review actions.
export const useIsPatientDeceased = ({ patient, deregistrations }) => {
  // Trigger (b): patient record flagged deceased.
  const isRecordDeceased = !!patient?.died || !!patient?.dateOfDeath;

  // Trigger (a): reason lives only on the per-review detail (not the status
  // summary DTO), so fetch the latest deregistration when one exists.
  const latestDeregistrationId = getLatestDeregistrationId(deregistrations);
  const { data: latestDeregistration } = useGetCDM(latestDeregistrationId);
  const isDeregisteredAsDeceased = isDiedDeregistration(latestDeregistration);

  return isRecordDeceased || isDeregisteredAsDeceased;
};
